// The pickers of the card and the selection bar (MacPickers.swift): the keyboard starts in a field that narrows the
// choices and reads a typed day or time as Quick Entry does, ↑ ↓ and Return choose, a calendar picks any day, Esc closes.

import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { moveDestinations, type MoveDestination } from "../core/actions";
import { deadlineChoiceDay, offeredDeadlineChoices, offeredWhenChoices, whenChoiceDay, type DeadlineChoiceID, type WhenChoiceID } from "../core/choices";
import { addDays, addMonths, dayFrom, dayParts, weekday } from "../core/days";
import { dayIn, parse } from "../core/parser";
import type { Area, Project, Todo } from "../core/records";
import {
  monthPositionSuggestion, offersReminderScope, reminderClockFor, reminderMovesToTodayFor, repeatYearDay, shownRepeatMonthDays, shownRepeatWeekdays,
  skippedDay, toggledMonthDay, yearDayStart, type ReminderScope,
} from "../core/repeats";
import type { Day, MonthPosition, RepeatRule, Start, Weekday } from "../core/types";
import { key, t } from "../i18n";
import { formatDay, hourLabel, repeatLabel, shortDate, splitEmoji } from "./format";
import { Icon, Ring, type IconName } from "./Icon";
import { reminderDay, reminderHasPassed } from "./quickEntry";

interface Option {
  id: string;
  title: string;
  detail?: string;
  icon: IconName;
  tint?: string;
  /** The letter the Mac's picker had for it: still the first match of a single typed letter. */
  key?: string;
  selected?: boolean;
  /** Shown dimmed and skipped, such as a reminder time that has passed today. */
  disabled?: boolean;
  run: () => void;
}

/**
 * The keyboard starts in the field (docs/55, item 12): typing narrows the choices by their names ("t" Today, "tom"
 * Tomorrow) and reads a day or time at once ("thu", "12.10.", "in 3 days"; "9", "14:30"), ↑ ↓ move over what is
 * left, Return takes the highlight, Esc closes (the popover). A single letter that was a key on the Mac still finds
 * its choice first, but no longer picks it on its own.
 */
function FilterPicker({ options, label, placeholder, hint, read, onClose, onTab, highlightsFirst = true, before, children }: {
  options: Option[]; label: string; placeholder: string; hint: string;
  /** The typed text as a day or a time, if it reads as one: a choice above the names it matches. */
  read?: (text: string) => Option | Option[] | undefined;
  /** Return with nothing typed and nothing highlighted. */
  onClose?: () => void;
  /**
   * Off: with nothing chosen, nothing is highlighted at first, so Return doesn't apply the first choice to several
   * to-dos that differ (MacChoicePicker).
   */
  highlightsFirst?: boolean;
  /** ⇥ in the field, such as switching the reminder picker's tabs (This One, Future Ones). */
  onTab?: () => void;
  /** Above the field, such as those tabs. */
  before?: ReactNode;
  children?: ReactNode;
}) {
  const [text, setText] = useState("");
  // -1: nothing highlighted, until the arrows move or something is typed.
  const opening = () => {
    const chosen = options.findIndex((option) => option.selected && !option.disabled);
    return chosen >= 0 ? chosen : highlightsFirst ? options.findIndex((option) => !option.disabled) : -1;
  };
  const [index, setIndex] = useState(opening);
  // The choices changed while open (the reminder's tabs): what is chosen now is highlighted, as on the Mac.
  const chosenID = options.find((option) => option.selected && !option.disabled)?.id;
  const lastChosen = useRef(chosenID);
  useEffect(() => {
    if (lastChosen.current === chosenID) return;
    lastChosen.current = chosenID;
    if (text.trim() === "") setIndex(opening());
  }, [chosenID]);
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    field.current?.focus({ preventScroll: true });
  }, []);
  const needle = text.trim().toLowerCase();
  const shown = useMemo(() => {
    if (needle === "") return options;
    const byName = options.filter((option) => option.title.toLowerCase().startsWith(needle));
    const byKey = byName.length === 0 && needle.length === 1 ? options.filter((option) => option.key === needle) : [];
    const read_ = read?.(text.trim());
    const typed = read_ === undefined ? [] : Array.isArray(read_) ? read_ : [read_];
    const named = [...byName, ...byKey];
    return [...typed.filter((option) => !named.some((other) => other.id === option.id)), ...named];
  }, [options, needle, text, read]);
  const highlighted = index < 0 ? undefined : shown[Math.min(index, shown.length - 1)];
  const key = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.nativeEvent.isComposing) return;
    if (event.key === "Tab" && onTab) {
      event.preventDefault();
      onTab();
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (shown.length === 0) return;
      const step = event.key === "ArrowDown" ? 1 : shown.length - 1;
      // To the next choice that can be taken; from nothing, ↓ goes to the first and ↑ to the last.
      setIndex((current) => {
        let next = current < 0 ? (step === 1 ? shown.length - 1 : 0) : Math.min(current, shown.length - 1);
        for (let tries = 0; tries < shown.length; tries++) {
          next = (next + step) % shown.length;
          if (!shown[next]?.disabled) return next;
        }
        return current;
      });
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (highlighted) {
        if (!highlighted.disabled) highlighted.run();
      } else if (needle === "") {
        onClose?.();
      }
    }
  };
  const listID = useId();
  return (
    <>
      {before}
      <div className="picker-field">
        <input ref={field} value={text} placeholder={placeholder} aria-label={label} autoComplete="off" spellCheck={false}
          role="combobox" aria-expanded="true" aria-controls={listID} aria-autocomplete="list"
          aria-activedescendant={highlighted ? `${listID}-${highlighted.id}` : undefined}
          onChange={(event) => { setText(event.target.value); setIndex(0); }} onKeyDown={key} />
      </div>
      <p className="picker-hint">{hint}</p>
      <div id={listID} className="picker-list" role="listbox" aria-label={label}>
        {/* A pointer that moves highlights; a row that opens or slides under a still one does not take the highlight
            from the keyboard (as on the Mac). */}
        {shown.map((option) => (
          <div key={option.id} id={`${listID}-${option.id}`} role="option" aria-selected={option.selected === true} aria-disabled={option.disabled || undefined}
            className={`picker-option${option === highlighted ? " highlighted" : ""}${option.selected ? " chosen" : ""}${option.disabled ? " disabled" : ""}`}
            onMouseMove={() => { if (!option.disabled) setIndex(shown.indexOf(option)); }} onClick={() => { if (!option.disabled) option.run(); }}>
            <span className="picker-icon" style={option.tint ? { color: option.tint } : undefined}><Icon name={option.icon} /></span>
            <span className="picker-title">{option.title}</span>
            {option.detail && <span className="picker-detail">{option.detail}</span>}
          </div>
        ))}
        {shown.length === 0 && <p className="picker-note">{t("Nothing matches.")}</p>}
      </div>
      {children}
    </>
  );
}

/** The typed text as a day ("fri", "tomorrow", "12.10.", "in 3 days"; QuickParser.day(in:)), as a choice. */
function readDay(text: string, today: Day, icon: IconName, tint: string | undefined, onDay: (day: Day) => void): Option | undefined {
  const day = dayIn(text, today);
  if (day === undefined || day < today) return undefined;
  return { id: `day-${day}`, title: formatDay(day, { weekday: "long", day: "numeric", month: "long" }), icon, run: () => onDay(day), ...(tint ? { tint } : {}) };
}

/** A typed repeat that names its first day, as Quick Entry reads it: "every 15th", "jeden 1. und 15.", "every Monday". */
function readRepeat(text: string, today: Day, onRepeat: (rule: RepeatRule, day: Day) => void): Option | undefined {
  if (text === "") return undefined;
  const token = parse("x " + text, { today }).tokens.find((found) => found.kind === "repeats");
  if (token?.kind !== "repeats" || token.value.start === undefined) return undefined;
  const { rule, start } = token.value;
  // Its first day is the row below it, the day alone; the popover is too narrow for both on one line.
  return { id: `repeat-${JSON.stringify(rule)}`, title: repeatLabel(rule), icon: "repeat", run: () => onRepeat(rule, start) };
}

/** A month to pick a day in, starting on Monday (or Sunday where the language does). */
export function Calendar({ selected, today, onDay }: { selected?: Day; today: Day; onDay: (day: Day) => void }) {
  const [month, setMonth] = useState(() => {
    const { year, month: m } = dayParts(selected ?? today);
    return dayFrom(year, m, 1);
  });
  const firstWeekday: Weekday = 2;
  const offset = (weekday(month) - firstWeekday + 7) % 7;
  const start = addDays(month, -offset);
  const days = Array.from({ length: 42 }, (_, index) => addDays(start, index));
  const { month: shown } = dayParts(month);
  const weekdayNames = Array.from({ length: 7 }, (_, index) => formatDay(addDays(start, index), { weekday: "narrow" }));
  return (
    <div className="calendar">
      <div className="calendar-head">
        <span>{formatDay(month, { month: "long", year: "numeric" })}</span>
        <button aria-label={t("Previous Month")} onClick={() => setMonth(addMonths(month, -1))}><Icon name="chevLeft" /></button>
        <button aria-label={t("Next Month")} onClick={() => setMonth(addMonths(month, 1))}><Icon name="chevRight" /></button>
      </div>
      <div className="calendar-grid" role="grid">
        {weekdayNames.map((name, index) => <span key={`w${index}`} className="calendar-weekday" aria-hidden="true">{name}</span>)}
        {days.map((day) => (
          <button key={day} className={`calendar-day${dayParts(day).month !== shown ? " other" : ""}${day === today ? " today" : ""}${day === selected ? " chosen" : ""}`}
            disabled={day < today} aria-label={formatDay(day, { weekday: "long", day: "numeric", month: "long" })} aria-pressed={day === selected}
            onClick={() => onDay(day)}>
            {dayParts(day).date}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * When a to-do starts (WhenChoice): no Inbox, which is a place, not a time; Move takes a to-do back there (Bastian,
 * 2026-10-13). `start` is undefined for several to-dos that differ: nothing shows as chosen or is highlighted.
 */
export function WhenPicker({ start, today, onChoose, onClose, onRepeat }: {
  start: Start | undefined; today: Day; onChoose: (start: Start) => void; onClose?: () => void;
  /** A typed repeat that names its day ("every 15th", "jeden Montag"): the first row sets it with its first day (docs/67, D). */
  onRepeat?: (rule: RepeatRule, day: Day) => void;
}) {
  const isOn = (day: Day) => start?.kind === "on" && start.day === day;
  const on = (choice: WhenChoiceID) => whenChoiceDay(choice, today) as Day;
  const detail = (choice: WhenChoiceID) => formatDay(on(choice), { weekday: "short", day: "numeric", month: "short" });
  const all: Record<WhenChoiceID, Option> = {
    today: { id: "today", title: t("Today"), icon: "today", tint: "var(--accent)", key: "t", selected: start?.kind === "on" && start.day <= today, run: () => onChoose({ kind: "on", day: today }) },
    tomorrow: { id: "tomorrow", title: t("Tomorrow"), icon: "sunrise", key: "m", selected: isOn(on("tomorrow")), run: () => onChoose({ kind: "on", day: on("tomorrow") }) },
    weekend: { id: "weekend", title: t("Weekend"), detail: detail("weekend"), icon: "umbrella", key: "w", selected: isOn(on("weekend")), run: () => onChoose({ kind: "on", day: on("weekend") }) },
    nextWeek: { id: "nextWeek", title: t("Next Week"), detail: detail("nextWeek"), icon: "nextWeek", key: "n", selected: isOn(on("nextWeek")), run: () => onChoose({ kind: "on", day: on("nextWeek") }) },
    anytime: { id: "anytime", title: t("Anytime"), icon: "anytime", key: "a", selected: start?.kind === "anytime", run: () => onChoose({ kind: "anytime" }) },
    someday: { id: "someday", title: t("Someday"), icon: "someday", key: "s", selected: start?.kind === "someday", run: () => onChoose({ kind: "someday" }) },
  };
  // A choice on the same day as one above it is left out: the weekend on a Friday, next week on a Sunday.
  const options: Option[] = offeredWhenChoices(today).map((choice) => all[choice]);
  const onDay = (day: Day) => onChoose({ kind: "on", day });
  return (
    <div className="picker">
      <FilterPicker label={t("When")} options={options} placeholder={t("Type: tom, fri, 12.10., in 3 days")} hint={t("Type to narrow, or a day. ↑↓ and Return choose.")}
        read={(text) => {
          const day = readDay(text, today, "calendar", undefined, onDay);
          const repeat = onRepeat && readRepeat(text, today, onRepeat);
          return repeat ? (day ? [repeat, day] : [repeat]) : day;
        }} onClose={onClose} highlightsFirst={start !== undefined}>
        <Calendar selected={start?.kind === "on" ? start.day : undefined} today={today} onDay={onDay} />
      </FilterPicker>
    </div>
  );
}

export function DeadlinePicker({ deadline, today, onChoose }: { deadline?: Day; today: Day; onChoose: (day: Day | undefined) => void }) {
  const red = "var(--deadline)";
  const titles: Record<DeadlineChoiceID, { title: (day: Day) => string; detail?: (day: Day) => string; key: string }> = {
    today: { title: () => t("Today"), key: "t" },
    tomorrow: { title: () => t("Tomorrow"), key: "m" },
    friday: { title: (day) => formatDay(day, { weekday: "long" }), detail: (day) => shortDate(day, today), key: "f" },
    inAWeek: { title: () => t("In a Week"), key: "w" },
    endOfMonth: { title: () => t("End of Month"), detail: (day) => shortDate(day, today), key: "e" },
  };
  // A choice on the same day as one above it is left out, as Friday on a Thursday.
  const options: Option[] = offeredDeadlineChoices(today).map((choice) => {
    const day = deadlineChoiceDay(choice, today);
    const { title, detail, key } = titles[choice];
    return { id: choice === "inAWeek" ? "week" : choice, title: title(day), ...(detail ? { detail: detail(day) } : {}), icon: "due", tint: red, key, selected: deadline === day, run: () => onChoose(day) };
  });
  if (deadline !== undefined) options.push({ id: "remove", title: t("Remove Deadline"), icon: "x", key: "r", run: () => onChoose(undefined) });
  return (
    <div className="picker">
      <FilterPicker label={t("Deadline")} options={options} placeholder={t("Type: tom, fri, 12.10., in 3 days")} hint={t("Type to narrow, or a day. ↑↓ and Return choose.")}
        read={(text) => readDay(text, today, "due", red, onChoose)}>
        <Calendar selected={deadline} today={today} onDay={onChoose} />
      </FilterPicker>
    </div>
  );
}

const PRESETS: { title: string; hour: number; icon: IconName; key: string }[] = [
  { title: key("Morning"), hour: 9, icon: "sunrise", key: "m" },
  { title: key("Noon"), hour: 12, icon: "today", key: "n" },
  { title: key("Afternoon"), hour: 15, icon: "cup", key: "a" },
  { title: key("Evening"), hour: 18, icon: "sunset", key: "e" },
];

/**
 * A reminder on the to-do's day (or today, when it is overdue or has none): a common time by its name, or a typed one
 * ("14:30", "3pm", "15 Uhr", "9"). A time that has passed is dimmed and refused, as on every other device. A to-do that
 * repeats and reminds has two tabs on top (docs/62): This One, where it opens, and Future Ones, which shows the series'
 * time and sets only that; each changes the time it shows, and ⇥ switches them. The first reminder of a repeating to-do
 * is both, without tabs. The tab's time that is set, when it is none of the common times, is the first row, chosen, so
 * the picker opens on it and Return keeps it (Bastian, 2026-10-21; MacReminderPicker).
 */
export function ReminderPicker({ todo, today, zone, onChoose, onKeep = () => {} }: {
  todo: Pick<Todo, "start" | "repeats" | "reminder" | "reminderZone" | "seriesReminder" | "seriesReminderZone">; today: Day; zone: string;
  onChoose: (time: { hour: number; minute: number } | undefined, scope: ReminderScope) => void;
  /** The time that is set, taken again: nothing changes, the picker closes. */
  onKeep?: () => void;
}) {
  const [scope, setScope] = useState<ReminderScope>("this");
  const scoped = offersReminderScope(todo);
  const day = reminderDay(todo, today);
  const passed = (time: { hour: number; minute: number }) => reminderHasPassed(todo, time.hour, time.minute, today, zone, scope);
  const set = reminderClockFor(todo, scope, zone);
  const isSet = (time: { hour: number; minute: number }) => set?.hour === time.hour && set.minute === time.minute;
  const setRow: Option | undefined = set && !PRESETS.some((preset) => isSet({ hour: preset.hour, minute: 0 }))
    ? { id: "set", title: t("Set"), detail: hourLabel(set.hour, set.minute), icon: "bell", tint: "var(--accent)", selected: true, run: onKeep }
    : undefined;
  const readTime = (text: string): Option | undefined => {
    const trimmed = text.trim();
    if (trimmed === "") return undefined;
    const withAt = /^\d/.test(trimmed) && !/[:.]/.test(trimmed) && !/(am|pm|uhr|h)$/i.test(trimmed) ? `at ${trimmed}` : trimmed;
    for (const token of parse(`x ${withAt}`, { today: day }).tokens) {
      if (token.kind === "time") {
        const time = token.value;
        // The time that is set, typed: its row, which keeps it.
        if (setRow && isSet(time)) return setRow;
        return passed(time)
          ? { id: `time-${time.hour}-${time.minute}`, title: t("This time has passed"), detail: hourLabel(time.hour, time.minute), icon: "clock", disabled: true, run: () => {} }
          : { id: `time-${time.hour}-${time.minute}`, title: hourLabel(time.hour, time.minute), icon: "clock", tint: "var(--accent)", run: () => onChoose(time, scope) };
      }
    }
    return undefined;
  };
  // The time that is set stays the highlight even when it has passed today: Return keeps it.
  const options: Option[] = PRESETS.map((preset) => {
    const time = { hour: preset.hour, minute: 0 };
    return {
      id: preset.title, title: t(preset.title), detail: hourLabel(preset.hour), icon: preset.icon, tint: "var(--accent)", key: preset.key,
      selected: isSet(time), disabled: !isSet(time) && passed(time),
      run: () => (passed(time) ? onKeep() : onChoose(time, scope)),
    };
  });
  if (setRow) options.unshift(setRow);
  if (set) options.push({ id: "remove", title: t("Remove Reminder"), icon: "bellSlash", key: "r", run: () => onChoose(undefined, scope) });
  const other = scope === "this" ? "future" : "this";
  const tabs = scoped && (
    <div className="segmented reminder-scope" role="tablist" aria-label={t("Reminder")}>
      {SCOPES.map((item) => (
        <button key={item.scope} role="tab" aria-selected={scope === item.scope} className={scope === item.scope ? "on" : ""}
          onMouseDown={(event) => event.preventDefault()} onClick={() => setScope(item.scope)}>
          {t(item.title)}
        </button>
      ))}
    </div>
  );
  return (
    <div className="picker">
      <FilterPicker label={t("Reminder")} options={options} placeholder={t("Type: 9, 14:30, 3pm")} read={readTime}
        hint={scoped ? t("Type to narrow, or a time. ↑↓ and Return choose, ⇥ switches the tab.") : t("Type to narrow, or a time. ↑↓ and Return choose.")}
        before={tabs} {...(scoped ? { onTab: () => setScope(other) } : {})} />
      {/* Said before it happens (Bastian, 2026-10-20): a reminder goes off on the to-do's day. Future Ones leaves this one where it is. */}
      {reminderMovesToTodayFor(todo, scope, today) && <p className="picker-note moves-to-today"><Icon name="today" /> {t("Also moves it to Today")}</p>}
      <p className="picker-note">{t("It goes off on your iPhone, iPad, Mac and Watch.")}</p>
    </div>
  );
}

const SCOPES: { scope: ReminderScope; title: string }[] = [
  { scope: "this", title: key("This One") }, { scope: "future", title: key("Future Ones") },
];

const UNITS: { unit: RepeatRule["unit"] | null; title: string }[] = [
  { unit: null, title: key("Never") }, { unit: "day", title: key("Daily") }, { unit: "week", title: key("Weekly") }, { unit: "month", title: key("Monthly") }, { unit: "year", title: key("Yearly") },
];

/**
 * How often it comes back: the unit, every how many, on which days, from completion, until when; skip once. It names
 * its day (docs/67): Weekly shows the start's weekday chosen, Monthly chooses days of the month (On Days) or a place in
 * the month (On a Weekday), Yearly its date; the Starts row says where the first one falls and where it moved from.
 * Each day chosen looks from the start the picker opened with (`origin`), as the apps' Repeat sheet does.
 */
export function RepeatPicker({ todo, today, onChange, onStart, onSkip }: {
  todo: Todo; today: Day;
  /** The rule, and the day the start is aligned from when a day was chosen. */
  onChange: (rule: RepeatRule | undefined, origin?: Day) => void;
  /** The start set from here: the Starts row, a yearly repeat's date. */
  onStart?: (day: Day) => void;
  onSkip?: () => void;
}) {
  const rule = todo.repeats;
  const order: Weekday[] = [2, 3, 4, 5, 6, 7, 1];
  // The start the picker opened with: every day chosen looks from here, and the Starts row says when it moved.
  const [origin] = useState<Day | undefined>(() => (todo.start.kind === "on" ? todo.start.day : undefined));
  const set = (change: Partial<RepeatRule>) => rule && onChange(cleanRule({ ...rule, ...change }));
  const setDays = (next: RepeatRule) => onChange(cleanRule(next), origin);
  const skipTo = rule ? skippedDay(todo, { today }) : null;
  const start = todo.start.kind === "on" ? todo.start.day : undefined;
  const shownWeekdays = shownRepeatWeekdays(todo, today);
  return (
    <div className="picker repeat-picker">
      <div className="segmented" role="radiogroup" aria-label={t("Repeat")}>
        {UNITS.map((item) => (
          <button key={item.title} role="radio" aria-checked={(rule?.unit ?? null) === item.unit}
            className={(rule?.unit ?? null) === item.unit ? "on" : ""}
            onClick={() => {
              // The unit that is set already keeps its rule: its weekdays and Count From Completion stay.
              if ((rule?.unit ?? null) === item.unit) return;
              onChange(item.unit === null ? undefined : { interval: rule?.interval ?? 1, unit: item.unit, afterCompletion: false, ...(rule?.endsOn ? { endsOn: rule.endsOn } : {}) });
            }}>
            {t(item.title)}
          </button>
        ))}
      </div>
      {rule && (
        <>
          <div className="repeat-row">
            <span className="repeat-every">{repeatLabel({ ...rule, weekdays: undefined, monthPosition: undefined, monthDays: undefined })}</span>
            <span className="stepper">
              <button aria-label={t("Less Often")} disabled={rule.interval <= 1} onClick={() => set({ interval: rule.interval - 1 })}>−</button>
              <button aria-label={t("More Often")} disabled={rule.interval >= 99} onClick={() => set({ interval: rule.interval + 1 })}>+</button>
            </span>
          </div>
          {rule.unit === "week" && (
            <div className="weekday-chips" role="group" aria-label={t("Days")}>
              {order.map((day) => {
                // Without days of its own, the start's weekday shows chosen; the first tap chooses the day tapped instead.
                const on = shownWeekdays.includes(day);
                const stored = rule.weekdays ?? [];
                return (
                  <button key={day} aria-pressed={on} className={on ? "on" : ""} title={formatDay(addDays("2026-10-04" as Day, day - 1), { weekday: "long" })}
                    onClick={() => setDays({ ...rule, weekdays: stored.includes(day) ? stored.filter((other) => other !== day) : [...stored, day].sort() as Weekday[] })}>
                    {formatDay(addDays("2026-10-04" as Day, day - 1), { weekday: "narrow" })}
                  </button>
                );
              })}
            </div>
          )}
          {rule.unit === "month" && <MonthPlace rule={rule} todo={todo} today={today} set={setDays} />}
          {rule.unit === "year" && onStart && <YearDay todo={todo} today={today} origin={origin} onStart={onStart} />}
          {onStart && start !== undefined && (
            <div className="repeat-row repeat-starts">
              <span>{t("Starts")}</span>
              <span className="repeat-start">
                <input type="date" aria-label={t("Starts")} value={start} min={today}
                  onChange={(event) => event.target.value && onStart(event.target.value as Day)} />
                {origin !== undefined && origin !== start && (
                  <span className="picker-detail repeat-moved">{t("Moved from {day}", { day: formatDay(origin, { weekday: "short", day: "numeric", month: "short" }) })}</span>
                )}
              </span>
            </div>
          )}
          {!(rule.weekdays && rule.weekdays.length) && (
            <label className="toggle-row">
              <input type="checkbox" checked={rule.afterCompletion} onChange={(event) => set({ afterCompletion: event.target.checked })} />
              <span>{t("Count From Completion")}</span>
            </label>
          )}
          <div className="repeat-row">
            <span>{t("Ends")}</span>
            <span className="ends">
              <select aria-label={t("Ends")} value={rule.endsOn ? "on" : "never"}
                onChange={(event) => set({ endsOn: event.target.value === "on" ? addMonths(today, 3) : undefined })}>
                <option value="never">{t("Never")}</option>
                <option value="on">{t("On a Day")}</option>
              </select>
              {rule.endsOn && (
                <input type="date" aria-label={t("Ends On")} value={rule.endsOn} min={today}
                  onChange={(event) => event.target.value && set({ endsOn: event.target.value as Day })} />
              )}
            </span>
          </div>
          <p className="picker-note repeat-summary">{repeatLabel(rule)}</p>
          {onSkip && skipTo && (
            <button className="outline-button small" onClick={onSkip}>
              <Icon name="skip" />{t("Skip This Time")}<span className="picker-detail">{t("Next: {day}", { day: shortDate(skipTo, today) })}</span>
            </button>
          )}
        </>
      )}
    </div>
  );
}

/** Yearly: on the start's date; another date moves the start to its next one (Todo.setRepeatYearDay). */
function YearDay({ todo, today, origin, onStart }: { todo: Todo; today: Day; origin?: Day; onStart: (day: Day) => void }) {
  const { month, day } = repeatYearDay(todo, today);
  const length = new Date(Date.UTC(2028, month, 0)).getUTCDate();
  const choose = (nextMonth: number, nextDay: number) => onStart(yearDayStart(todo, nextMonth, nextDay, today, origin));
  return (
    <div className="repeat-row">
      <span>{t("Yearly On")}</span>
      <span className="repeat-year">
        <select aria-label={t("Month")} value={month} onChange={(event) => choose(Number(event.target.value), day)}>
          {Array.from({ length: 12 }, (_, index) => (
            <option key={index} value={index + 1}>{formatDay(dayFrom(2026, index + 1, 1), { month: "long" })}</option>
          ))}
        </select>
        <select aria-label={t("Day")} value={day} onChange={(event) => choose(month, Number(event.target.value))}>
          {Array.from({ length }, (_, index) => <option key={index} value={index + 1}>{index + 1}</option>)}
        </select>
      </span>
    </div>
  );
}

const ORDINALS: { ordinal: number; title: string }[] = [
  { ordinal: 1, title: key("1st") }, { ordinal: 2, title: key("2nd") }, { ordinal: 3, title: key("3rd") }, { ordinal: 4, title: key("4th") }, { ordinal: -1, title: key("Last") },
];

/**
 * Monthly (docs/67): On Days, a grid of the days of the month (several may be chosen; 31 is shown as Last Day, the same
 * day every month), or On a Weekday, a place in the month, the third Monday or the last day (the apps' monthOptions).
 * Without days of its own the start's day shows chosen, and the first tap chooses the day tapped instead. The start
 * moves to the first such day (applyChange aligns it from the picker's origin).
 */
function MonthPlace({ rule, todo, today, set }: { rule: RepeatRule; todo: Todo; today: Day; set: (rule: RepeatRule) => void }) {
  const position = rule.monthPosition;
  const suggestion = monthPositionSuggestion(todo, today);
  const order: Weekday[] = [2, 3, 4, 5, 6, 7, 1];
  const shownDays = shownRepeatMonthDays(todo, today);
  const setPosition = (next: MonthPosition | undefined) => {
    const changed: RepeatRule = { ...rule };
    delete changed.monthDays;
    if (next === undefined) delete changed.monthPosition;
    else changed.monthPosition = next;
    set(changed);
  };
  const dayButton = (day: number, title: string, className = "") => {
    const on = shownDays.includes(day);
    return (
      <button key={day} aria-pressed={on} className={`${on ? "on" : ""} ${className}`.trim()} onClick={() => set(toggledMonthDay(rule, day))}>
        {title}
      </button>
    );
  };
  return (
    <>
      <div className="segmented month-place" role="radiogroup" aria-label={t("Monthly On")}>
        <button role="radio" aria-checked={!position} className={!position ? "on" : ""} onClick={() => position && setPosition(undefined)}>
          {t("On Days")}
        </button>
        <button role="radio" aria-checked={!!position} className={position ? "on" : ""} onClick={() => !position && setPosition(suggestion)}>
          {t("On a Weekday")}
        </button>
      </div>
      {!position && (
        <div className="month-days" role="group" aria-label={t("Days of the Month")}>
          {Array.from({ length: 30 }, (_, index) => dayButton(index + 1, String(index + 1)))}
          {dayButton(31, t("Last Day"), "last-day")}
        </div>
      )}
      {position && (
        <>
          <div className="weekday-chips" role="group" aria-label={t("Monthly On")}>
            {ORDINALS.map((item) => (
              <button key={item.ordinal} aria-pressed={position.ordinal === item.ordinal} className={position.ordinal === item.ordinal ? "on" : ""}
                onClick={() => {
                  // A day of the month exists only as the first or the last.
                  const weekday = position.weekday === undefined && item.ordinal !== 1 && item.ordinal !== -1 ? suggestion.weekday : position.weekday;
                  setPosition({ ordinal: item.ordinal, ...(weekday !== undefined ? { weekday } : {}) });
                }}>
                {t(item.title)}
              </button>
            ))}
          </div>
          <div className="weekday-chips" role="group" aria-label={t("Days")}>
            {order.map((day) => (
              <button key={day} aria-pressed={position.weekday === day} className={position.weekday === day ? "on" : ""}
                title={formatDay(addDays("2026-10-04" as Day, day - 1), { weekday: "long" })} onClick={() => setPosition({ ordinal: position.ordinal, weekday: day })}>
                {formatDay(addDays("2026-10-04" as Day, day - 1), { weekday: "narrow" })}
              </button>
            ))}
            <button aria-pressed={position.weekday === undefined} className={position.weekday === undefined ? "on" : ""} title={t("Day of the Month")}
              onClick={() => setPosition({ ordinal: position.ordinal === 1 ? 1 : -1 })}>
              {/* The day of the month, "Tag" (the lower-case key, capitalized here). */}
              <span className="capitalized">{t("day")}</span>
            </button>
          </div>
        </>
      )}
    </>
  );
}

function cleanRule(rule: RepeatRule): RepeatRule {
  const result: RepeatRule = { ...rule };
  if (result.weekdays !== undefined && result.weekdays.length === 0) delete result.weekdays;
  if (result.unit !== "week") delete result.weekdays;
  if (result.unit !== "month" || result.monthPosition === undefined) delete result.monthPosition;
  if (result.unit !== "month" || result.monthPosition !== undefined || !result.monthDays?.length) delete result.monthDays;
  if (result.endsOn === undefined) delete result.endsOn;
  if (result.weekdays) result.afterCompletion = false;
  return result;
}

/** Tags: those the account has, ticked when set, and a field for a new one. */
export function TagPicker({ tags, allTags, onToggle }: { tags: readonly string[]; allTags: readonly string[]; onToggle: (tag: string) => void }) {
  const [text, setText] = useState("");
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => field.current?.focus(), []);
  const needle = text.trim().toLowerCase();
  const shown = allTags.filter((tag) => tag.toLowerCase().includes(needle));
  const exists = allTags.some((tag) => tag.toLowerCase() === needle);
  return (
    <div className="picker">
      <div className="picker-field">
        <input ref={field} value={text} placeholder={t("Find or Add a Tag")} aria-label={t("Tag")}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && needle) {
              event.preventDefault();
              const match = allTags.find((tag) => tag.toLowerCase() === needle);
              onToggle(match ?? text.trim().replace(/^#/, ""));
              setText("");
            }
          }} />
      </div>
      <div className="tag-list" role="group" aria-label={t("Tags")}>
        {needle && !exists && (
          <button className="picker-option" onClick={() => { onToggle(text.trim().replace(/^#/, "")); setText(""); }}>
            <span className="picker-icon"><Icon name="plusThin" /></span><span className="picker-title">{t("Add “{name}”", { name: text.trim() })}</span>
          </button>
        )}
        {shown.map((tag) => (
          <button key={tag} className={`picker-option${tags.includes(tag) ? " chosen" : ""}`} aria-pressed={tags.includes(tag)} onClick={() => onToggle(tag)}>
            <span className="picker-icon"><Icon name="tag" /></span><span className="picker-title">{tag}</span>
            {tags.includes(tag) && <Icon name="check" className="picker-tick" />}
          </button>
        ))}
        {shown.length === 0 && !needle && <p className="picker-note">{t("No tags yet. Type one to add it.")}</p>}
      </div>
    </div>
  );
}

/**
 * Where to: the Inbox, Miscellaneous, each area and its projects (Store.moveDestinations), with a field to narrow. It
 * starts on the to-do's own place, or on nothing for several to-dos in different places, so Return with nothing typed
 * closes it without a change (MacMovePicker). `areasOnly`: the areas alone, in the sidebar's order, for the area of a
 * project made from a to-do that has none (Convert to Project; Store.areaForProject).
 */
export function MovePicker({ areas, projects, current, onChoose, onClose, progress, areasOnly = false }: {
  areas: readonly Area[]; projects: readonly Project[]; current?: MoveDestination; onChoose: (destination: MoveDestination) => void; onClose: () => void;
  progress: (project: Project) => number; areasOnly?: boolean;
}) {
  const [text, setText] = useState("");
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => field.current?.focus(), []);
  const all = moveDestinations(areas, projects).filter((entry) => !areasOnly || entry.destination.kind === "area").map((entry) => {
    const d = entry.destination;
    const project = d.kind === "project" ? projects.find((p) => p.id === d.id) : undefined;
    const area = d.kind === "area" ? areas.find((a) => a.id === d.id) : undefined;
    const title = d.kind === "inbox" ? t("Inbox") : d.kind === "general" ? t("Miscellaneous") : project?.title ?? area?.title ?? "";
    return { ...entry, title, project, area };
  });
  const needle = text.trim().toLowerCase();
  const shown = needle ? all.filter((entry) => entry.title.toLowerCase().includes(needle)) : all;
  const same = (d: MoveDestination) => current !== undefined && current.kind === d.kind && ("id" in current ? current.id : "") === ("id" in d ? d.id : "");
  // -1: nothing highlighted, until the arrows move or something is typed.
  const [index, setIndex] = useState(() => all.findIndex((entry) => same(entry.destination)));
  return (
    <div className="picker move-picker">
      <div className="picker-field">
        <input ref={field} value={text} placeholder={areasOnly ? t("Find an Area") : t("Find a Project or Area")} aria-label={areasOnly ? t("Convert to Project") : t("Move")}
          onChange={(event) => { setText(event.target.value); setIndex(0); }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") { event.preventDefault(); setIndex((i) => Math.min(shown.length - 1, i + 1)); }
            if (event.key === "ArrowUp") { event.preventDefault(); setIndex((i) => Math.max(0, i - 1)); }
            if (event.key === "Enter") {
              event.preventDefault();
              const entry = shown[index];
              // Nothing chosen, or the to-do's own place with nothing typed: no change.
              if (!entry || (needle === "" && same(entry.destination))) onClose();
              else onChoose(entry.destination);
            }
          }} />
      </div>
      <div className="move-list" role="listbox" aria-label={areasOnly ? t("Convert to Project") : t("Move")}>
        {shown.map((entry, position) => {
          let mark: ReactNode;
          if (entry.destination.kind === "inbox") mark = <Icon name="inbox" />;
          else if (entry.destination.kind === "general") mark = <Icon name="misc" />;
          else if (entry.project) mark = entry.project.kind === "list" ? <Icon name="list" /> : entry.project.kind === "birthdays" ? <Icon name="gift" /> : <Ring progress={progress(entry.project)} size={16} />;
          else mark = <span className="emoji small">{splitEmoji(entry.title).emoji ?? ""}</span>;
          const title = entry.area ? splitEmoji(entry.title).name : entry.title;
          return (
            <div key={`${entry.destination.kind}-${"id" in entry.destination ? entry.destination.id : ""}`} role="option" aria-selected={same(entry.destination)}
              className={`picker-option level-${entry.level}${position === index ? " highlighted" : ""}${same(entry.destination) ? " chosen" : ""}${entry.area ? " strong" : ""}`}
              onMouseMove={() => setIndex(position)} onClick={() => onChoose(entry.destination)}>
              <span className="picker-icon">{mark}</span><span className="picker-title">{title}</span>
              {same(entry.destination) && <Icon name="check" className="picker-tick" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
